<script setup lang="ts">
import {
  AwaitingUploadIcon,
  ColdIcon,
  DocumentRejectedIcon,
  HotIcon,
  JPCExclusiveIcon,
  NewIcon,
  PendingReviewIcon,
  PromoIcon,
  validatedIcon,
} from '~/assets/data/icons';

export type TagTypes =
  | 'exclusive'
  | 'exclusive-tag'
  | 'promo'
  | 'new'
  | 'hot'
  | 'cold'
  | 'validated'
  | 'awaiting-upload'
  | 'document-rejected'
  | 'pending-review';

type TagPositions =
  | 'bottom-left'
  | 'bottom-right'
  | 'top-left'
  | 'top-right'
  | 'none';

interface ITagProps {
  type: TagTypes;
  position?: TagPositions;
  iconOnly?: boolean;
  iconDimensions?: number;
  iconClass?: string;
  titleClass?: string;
}

const props = withDefaults(defineProps<ITagProps>(), {
  type: 'exclusive',
  position: 'none',
  iconOnly: false,
  iconDimensions: 12,
  iconClass: '',
  titleClass: 'text-xs sm:text-sm',
});

const { getImage } = useBranding();

const determineIcon = computed(() => {
  switch (props?.type) {
    case 'exclusive':
      return getImage('exclusive');
    case 'exclusive-tag':
      return JPCExclusiveIcon;
    case 'promo':
      return PromoIcon;
    case 'new':
      return NewIcon;
    case 'hot':
      return HotIcon;
    case 'cold':
      return ColdIcon;
    case 'validated':
      return validatedIcon;
    case 'awaiting-upload':
      return AwaitingUploadIcon;
    case 'document-rejected':
      return DocumentRejectedIcon;
    case 'pending-review':
      return PendingReviewIcon;
    default:
      return '';
  }
});

const determineColor = computed(() => {
  switch (props?.type) {
    case 'exclusive-tag':
      return 'bg-primary-blue-900 !text-light-50 fill-light-50';
    case 'promo':
      return 'bg-primary-pink-100 !text-primary-pink-900';

    case 'new':
      return 'bg-primitive-gold-500 !text-primitive-gold-900';
    case 'hot':
      return 'bg-[#ff6767] text-[#600808]';
    case 'cold':
      return 'bg-primary-blue-100 !text-primary-blue-900';
    case 'validated':
      return 'bg-success-300 !text-success-900 fill-success-900';

    case 'awaiting-upload':
      return 'bg-grey-100 !text-grey-800 fill-grey-800';

    case 'document-rejected':
      return 'bg-error-100 !text-error-500 fill-error-500';

    case 'pending-review':
      return 'bg-warning-100 !text-amber-950 fill-amber-950';

    default:
      return 'hidden';
  }
});

const determinePosition = computed(() => {
  switch (props?.position) {
    case 'top-left':
      return 'absolute z-[1] top-0 left-0';

    case 'bottom-left':
      return 'absolute z-[1] bottom-1 left-1';

    case 'bottom-right':
      return 'absolute z-[1] bottom-2 right-0';
    case 'top-right':
      return 'absolute z-[1] top-0 right-1';

    default:
      return props.iconOnly ? 'p-1 sm:pl-1' : '';
  }
});
</script>
<template>
  <div
    v-if="props?.type === 'exclusive'"
    class="w-1/2 max-w-[100px]"
    :class="determinePosition"
  >
    <NuxtImg
      class="exclusive drop-shadow-[rgba(216,57,255,1)_0_5px_5px]"
      provider="kentico"
      :src="determineIcon"
      alt="exclusive"
      draggable="false"
    />
  </div>
  <div
    v-else
    :class="[
      'flex-center w-max rounded-md pl-1',
      determineColor,
      determinePosition,
    ]"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      :width="iconDimensions"
      :height="iconDimensions"
      :viewBox="`0 0 ${iconDimensions} ${iconDimensions}`"
      :class="props.iconClass"
    >
      <path :d="determineIcon" />
    </svg>
    <span
      class="my-0 sm:my-0.5 sm:leading-1 font-bold mr-1 capitalize ml-0.5"
      :class="titleClass + (iconOnly ? ' hidden sm:flex' : '')"
    >
      {{ $t(props?.type) }}
    </span>
  </div>
</template>
<style scoped lang="scss">
*:hover > .exclusive {
  @apply drop-shadow-[rgba(0,104,255,1)_0_5px_5px];
}
::selection {
  background: transparent;
}
::-moz-selection {
  background: transparent;
}
</style>
